import { Alert, Card, Checkbox, Col, Form, Input, Row, Select, Switch } from 'antd'
import React, { useState, useEffect } from 'react'
import { Breadcrumb, Button, Container } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Role, User } from '../../../models/User';
import { ControlPanelManager } from '../../../api/ControlPanelManager';
import { useForm } from 'antd/es/form/Form';
import { useToasts } from '../../../contexts/toasts';
import { TailSpin } from 'react-loader-spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { useMemo } from 'react';
import { Organization } from '../../../models/generics';
import { CustomBootstrapLinkType } from '../../../utils';

const formLayout = {
  wrapperCol: {
    xs: 24,
    sm: 18,
  },
  labelCol: {
    xs: 24,
    sm: 6,
  },
};

export default function UserEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<User>();
  const [userOrganizations, setUserOrganizations] = useState<string[]>([]);
  const [groups, setGroups] = useState<Organization[]>();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>();
  const [error, setError] = useState(false);
  const [form] = useForm();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const organizationsOptions = useMemo(() => groups?.map(group => ({ label: group.name, value: group.id })), [groups]);
  const rolesOptions = useMemo(() => roles?.map(role => ({ label: role.name, value: role.id })), [roles]);

  useEffect(() => {
    if (id) {
      initUser();
      initUserOrganizations();
      initUserRoles();
    }
    else setUser(new User());

    initGroups();
    initRoles();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (user) form.setFieldsValue({ ...user, admin: user.attributes?.realmAdmin !== undefined });
  }, [user, form]);

  const initUser = () => {
    ControlPanelManager.getUser(id!)
      .then(
        ({ data }) => setUser(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initUserRoles = () => {
    ControlPanelManager.getUserRoles(id!)
      .then(
        ({ data }) => {
          const realmRoles = data.realmMappings || [];
          setUserRoles(realmRoles.filter(role => role.composite === false).map(role => role.id!));
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-roles-error', message, `User Roles Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initRoles = () => {
    ControlPanelManager.getRoles()
      .then(
        ({ data }) => setRoles(data.filter(role => role.composite === false)),
        (err) => {
          setRoles([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('roles-error', message, `Roles Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initUserOrganizations = () => {
    ControlPanelManager.getUserGroups(id!)
      .then(
        ({ data }) => setUserOrganizations(data.map(org => org.id)),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-groups-error', message, `User Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )

  }

  const initGroups = () => {
    ControlPanelManager.getGroups()
      .then(
        ({ data }) => setGroups(data),
        (err) => {
          setGroups([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('groups-error', message, `Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const addUserRole = (newRole: string) => {
    showLoader();
    const role = roles!.find(r => r.id === newRole);

    ControlPanelManager.addRolesToUser(id!, [role as Role])
      .then(
        () => {
          hideLoader();
          setUserRoles(rr => rr.concat(newRole));
          showToast('user-role-added', 'New role assigned to user', 'Role Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-role-error', message, `Role Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const removeUserRole = (oldRole: string) => {
    showLoader();
    const role = roles!.find(r => r.id === oldRole);

    ControlPanelManager.removeRolesFromUser(id!, [role as Role])
      .then(
        () => {
          hideLoader();
          setUserRoles(rr => rr.filter(r => r !== oldRole));
          showToast('user-role-removed', 'Role unassigned to user', 'Role Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-role-error', message, `Role Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const addUserToGroup = (newGroup: string) => {
    showLoader();

    ControlPanelManager.addUserToGroup(id!, newGroup)
      .then(
        () => {
          hideLoader();
          setUserOrganizations(orgs => orgs.concat(newGroup));
          showToast('user-joined-group', 'User successfully joined organization', 'Organization Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const removeUserFromGroup = (oldGroup: string) => {
    showLoader();

    ControlPanelManager.removeUserFromGroup(id!, oldGroup)
      .then(
        () => {
          hideLoader();
          setUserOrganizations(orgs => orgs.filter(org => org !== oldGroup));
          showToast('user-left-group', 'User successfully left organization', 'Organization Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-group-error', message, `Group Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }


  const handleSubmit = (newUser: User) => {
    showLoader();

    const body = { ...user, ...newUser } as any;
    // setting credentials object based on password (user registration)
    if (!id) {
      body.credentials = [{ type: 'password', value: newUser.password, temporary: false }];
      delete body.password;

      if (userRoles.length) {
        body.roles = roles!.filter(r => userRoles.includes(r.id!));
      }
      // setting attributes based on user organization
      if (userOrganizations.length) {
        const organizationId = userOrganizations[0];
        const group = groups!.find(g => g.id === organizationId);
        body.attributes['organizationId'] = organizationId;
        body.attributes['organizationName'] = group!.name;
        body.groups = [group!.name];
      }
    }

    const promise = id
      ? ControlPanelManager.updateUser(body)
      : ControlPanelManager.createUser(body);

    promise.then(
      ({ data }) => {
        showToast('user-success', `User ${id ? 'updated' : 'created'} successfully`, 'User Success', 'success');
        hideLoader();
        navigate(`/control-panel/users-management/${data.id}`);
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('user-submit-error', message, `User ${id ? 'Update' : 'Creation'} Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }

  const renderUser = () => {
    return (
      <Form {...formLayout} form={form} onFinish={handleSubmit}>
        <Form.Item
          label="Username"
          name="username"
          rules={[{ required: true, message: "Username is required" }]}
        >
          <Input placeholder="Username" disabled={id !== undefined} />
        </Form.Item>

        {!id && (
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Password is required" }]}
          >
            <Input placeholder="Password" />
          </Form.Item>
        )}

        <Form.Item label=" " colon={false}>
          <Row align="middle">
            <Col xs={24} md={10} lg={4}>
              <Form.Item noStyle name="enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
              <span className="ms-2">Enable User</span>
            </Col>

            <Col xs={24} md={14} lg={20} className="mt-3 m-md-0">
              <Form.Item noStyle name="admin" valuePropName="checked">
                <Checkbox>Admin</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>


        <Form.Item label="Email">
          <Row align="middle">
            <Col xs={24} md={12} className="pe-md-3 mb-2 mb-md-0">
              <Form.Item noStyle name="email">
                <Input placeholder="Email" />
              </Form.Item>
            </Col>

            <Form.Item noStyle name="emailVerified" valuePropName="checked">
              <Checkbox>Email verified</Checkbox>
            </Form.Item>

            <Form.Item noStyle name="sendEmail" valuePropName="checked">
              <Checkbox>Send Email</Checkbox>
            </Form.Item>
          </Row>
        </Form.Item>


        <Form.Item label="First Name" name="firstName">
          <Input placeholder="User First Name" />
        </Form.Item>

        <Form.Item label="Last Name" name="lastName">
          <Input placeholder="User Last Name" />
        </Form.Item>

        <Form.Item label="Roles">
          <Select
            placeholder="Select User Roles"
            value={userRoles}
            allowClear
            mode="multiple"
            options={rolesOptions}
            onChange={id ? undefined : setUserRoles}
            onSelect={id ? addUserRole : undefined}
            onDeselect={id ? removeUserRole : undefined}
            loading={!rolesOptions}
          />
        </Form.Item>

        <Form.Item label="Organizations">
          <Select
            placeholder="Select User Organizations"
            value={userOrganizations}
            allowClear
            mode="multiple"
            options={organizationsOptions}
            onChange={id ? undefined : setUserOrganizations}
            onSelect={id ? addUserToGroup : undefined}
            onDeselect={id ? removeUserFromGroup : undefined}
            loading={!organizationsOptions}
          />
        </Form.Item>

        <br />

        <div className="d-flex justify-content-between">
          <Button as={Link as unknown as CustomBootstrapLinkType} to="/control-panel/users-management" variant="light" className="border" type="button" size="sm">
            <FontAwesomeIcon icon={solid('chevron-left')} />
            <span className="ms-2">Cancel</span>
          </Button>
          <Button variant="success" size="sm" type="submit">
            <FontAwesomeIcon icon={solid('check')} />
            <span className="ms-2">Confirm</span>
          </Button>
        </div>
      </Form>
    )
  }

  const renderAsyncContent = () => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occured while retrieving user. Try again later."
      />
    )

    return user
      ? renderUser()
      : (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      )
  }

  return (
    <Container className="py-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel' }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel/users-management' }}>User Management</Breadcrumb.Item>
        {id && <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/control-panel/users-management/${id}` }}>{user?.username}</Breadcrumb.Item>}
        <Breadcrumb.Item active>{id ? 'Edit' : 'Create'} User</Breadcrumb.Item>
      </Breadcrumb>

      <Card title={id ? 'Edit User' : 'New User'}>{renderAsyncContent()}</Card>
    </Container>
  )
}